import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  User,
  Mail,
  Lock,
  Phone,
  Eye,
  EyeOff,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Leaf,
  Clock,
} from "lucide-react";
import { sellerAPI } from "../API/axios";

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
}

/* ─── STEP 1: Registration Form ─── */
interface RegisterFormProps {
  onSuccess: (data: { email: string; userId: string }) => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = () => {
    const e: FormErrors = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      e.email = "Valid email required";
    if (!form.phone.match(/^[6-9]\d{9}$/))
      e.phone = "Valid 10-digit Indian mobile required";
    if (form.password?.length < 6) e.password = "Minimum 6 characters";
    if (form.password !== form.confirmPassword)
      e.confirmPassword = "Passwords do not match";
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await sellerAPI.register({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
      });
      toast.success(
        res.data.message ||
          "Registration successful! Check your email for OTP.",
      );
      onSuccess({ email: form.email, userId: res.data.userId });
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const set =
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((p) => ({ ...p, [field]: e.target.value }));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
            First Name
          </label>
          <div className="relative group">
            <User
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
            />
            <input
              id="reg-fname"
              className="form-input !pl-11"
              placeholder="first name"
              value={form.firstName}
              onChange={set("firstName")}
            />
          </div>
          {errors.firstName && (
            <span className="text-[10px] font-medium text-red-500 ml-1">
              {errors.firstName}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
            Last Name
          </label>
          <div className="relative group">
            <User
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
            />
            <input
              id="reg-lname"
              className="form-input !pl-11"
              placeholder="last name"
              value={form.lastName}
              onChange={set("lastName")}
            />
          </div>
          {errors.lastName && (
            <span className="text-[10px] font-medium text-red-500 ml-1">
              {errors.lastName}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
          Email Address
        </label>
        <div className="relative group">
          <Mail
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
          />
          <input
            id="reg-email"
            type="email"
            className="form-input !pl-11"
            placeholder="seller@example.com"
            value={form.email}
            onChange={set("email")}
          />
        </div>
        {errors.email && (
          <span className="text-[10px] font-medium text-red-500 ml-1">
            {errors.email}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
          Phone Number
        </label>
        <div className="relative group">
          <Phone
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
          />
          <input
            id="reg-phone"
            type="tel"
            className="form-input !pl-11"
            placeholder="0000000000"
            maxLength={10}
            value={form.phone}
            onChange={set("phone")}
          />
        </div>
        {errors.phone && (
          <span className="text-[10px] font-medium text-red-500 ml-1">
            {errors.phone}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
          Password
        </label>
        <div className="relative group">
          <Lock
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
          />
          <input
            id="reg-password"
            type={showPass ? "text" : "password"}
            className="form-input !px-11"
            placeholder="Min. 6 characters"
            value={form.password}
            onChange={set("password")}
          />
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-primary transition-colors"
            onClick={() => setShowPass(!showPass)}
          >
            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.password && (
          <span className="text-[10px] font-medium text-red-500 ml-1">
            {errors.password}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
          Confirm Password
        </label>
        <div className="relative group">
          <Lock
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
          />
          <input
            id="reg-cpassword"
            type="password"
            className="form-input !pl-11"
            placeholder="Repeat password"
            value={form.confirmPassword}
            onChange={set("confirmPassword")}
          />
        </div>
        {errors.confirmPassword && (
          <span className="text-[10px] font-medium text-red-500 ml-1">
            {errors.confirmPassword}
          </span>
        )}
      </div>

      <button
        id="reg-submit"
        type="submit"
        className="btn btn-primary w-full py-3.5 mt-2 font-bold text-sm"
        disabled={loading}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Creating Account...</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span>Create Account</span>
            <ArrowRight size={18} />
          </div>
        )}
      </button>
    </form>
  );
};

/* ─── STEP 2: OTP Verification ─── */
interface OTPVerifyProps {
  email: string | undefined;
  onSuccess: () => void;
}

const OTPVerify: React.FC<OTPVerifyProps> = ({ email, onSuccess }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer effect
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCooldown]);

  const handleResendOTP = async () => {
    if (!email) {
      toast.error("Email not found. Please try registering again.");
      return;
    }

    if (resendCooldown > 0) {
      toast.info(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }

    setResendLoading(true);
    try {
      const response = await sellerAPI.resendOTP({ email });

      // Check if response has the message
      if (response.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success("OTP resent successfully! Please check your email.");
      }

      // Set cooldown (e.g., 30 seconds)
      setResendCooldown(30);
    } catch (err: any) {
      console.error("Resend OTP error:", err);

      // Handle specific error messages from backend
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to resend OTP. Please try again.";

      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerify = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (otp.length < 4) {
      toast.error("Enter the OTP sent to your email");
      return;
    }
    setLoading(true);
    try {
      await sellerAPI.verifyEmail({ email, otp });
      toast.success("Email verified successfully! 🎉");
      onSuccess();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleVerify}
      className="flex flex-col gap-8 mt-12 animate-slide-up"
    >
      <div className="flex items-start gap-4 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-slate-900 group">
        <CheckCircle2 size={24} className="text-emerald-500 shrink-0 mt-0.5" />
        <div className="flex flex-col gap-1">
          <div className="font-bold text-sm">Check your inbox</div>
          <div className="text-xs text-slate-500 leading-relaxed">
            We sent a 6-digit OTP to{" "}
            <strong className="text-primary font-bold">{email}</strong>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">
          Enter OTP
        </label>
        <input
          id="otp-input"
          className="form-input !text-3xl !py-4 !tracking-[0.5em] text-center font-bold"
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="••••••"
          value={otp}
          onChange={(e) =>
            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
        />
      </div>

      <div className="flex flex-col gap-3">
        <button
          id="otp-verify-btn"
          type="submit"
          className="btn btn-primary w-full py-4 text-sm font-bold tracking-wide"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Verifying...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span>Verify Email</span>
              <CheckCircle2 size={18} />
            </div>
          )}
        </button>

        <button
          id="otp-resend-btn"
          type="button"
          className="btn btn-ghost w-full py-4 text-sm font-bold border-none hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
          onClick={handleResendOTP}
          disabled={loading || resendLoading || resendCooldown > 0}
        >
          {resendLoading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-primary rounded-full animate-spin" />
              <span>Resending...</span>
            </div>
          ) : resendCooldown > 0 ? (
            <span className="text-slate-400">
              Resend OTP{" "}
              <span className="ml-1 text-primary">({resendCooldown}s)</span>
            </span>
          ) : (
            <span>Resend OTP</span>
          )}
        </button>
      </div>
    </form>
  );
};

/* ─── MAIN REGISTER PAGE ─── */
const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=register, 2=verify
  const [regData, setRegData] = useState<{
    email: string;
    userId: string;
  } | null>(null);

  const STEPS = [
    { label: "Register", num: 1 },
    { label: "Verify Email", num: 2 },
  ];

  return (
    <div className="min-h-screen bg-white relative flex flex-col page-root">
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-primary/5 rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-secondary/3 rounded-full blur-[120px] -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left panel */}
        <div className="hidden lg:flex flex-col p-8 bg-slate-50 border-r border-slate-100 overflow-y-auto -mt-20">
          {/* <div className="flex items-center gap-4 mb-20 group">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white shadow-xl shadow-primary/20 group-hover:scale-105 transition-transform">
              <Leaf size={26} />
            </div>
            <div className="flex flex-col">
              <div className="font-display text-xl font-bold bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent">
                Harappa bioscience
              </div>
              <div className="text-xs text-slate-400 tracking-wider font-medium uppercase">
                Seller Portal
              </div>
            </div>
          </div> */}

          <div className="grow flex flex-col justify-center max-w-lg -mt-18">
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-900 mb-8">
              Join 1,200+ sellers growing their bioscience business
            </h2>
            <div className="grid gap-3">
              {[
                "Pan-India reach from day one",
                "GST & KYC verified marketplace",
                "Payments within 7 working days",
                "Dedicated seller support team",
                "Real-time sales analytics dashboard",
              ].map((p) => (
                <div
                  key={p}
                  className="flex items-center gap-3 p-4 bg-white/50 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-white hover:border-primary transition-all group"
                >
                  <CheckCircle2
                    size={20}
                    className="text-emerald-500 shrink-0 group-hover:scale-110 transition-transform"
                  />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-auto text-xs font-semibold text-slate-400 uppercase tracking-widest pt-8">
            <ShieldCheck size={18} className="text-emerald-500" />
            <span>100% Secure · GST Compliant · Trusted Platform</span>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex items-center justify-center p-6 sm:p-12 sm:pt-24 lg:pt-12 overflow-y-auto">
          <div className="w-full max-w-md animate-slide-up">
            {/* Step indicator */}
            <div className="step-bar mt-8 mb-4 flex items-center gap-6">
              {STEPS.map(({ label, num }, idx) => (
                <React.Fragment key={num}>
                  <div className="step-item">
                    <div
                      className={`step-num ${
                        step > num
                          ? "done"
                          : step === num
                            ? "active"
                            : "inactive"
                      }`}
                    >
                      {step > num ? <CheckCircle2 size={16} /> : num}
                    </div>
                    <span
                      className={`step-label ${
                        step === num ? "active" : step > num ? "done" : ""
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < STEPS?.length - 1 && (
                    <div
                      className={`step-connector ${step > num ? "done" : ""}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="mb-6">
              <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">
                {step === 1 ? "Create Seller Account" : "Verify Your Email"}
              </h1>
              <p className="text-slate-500 text-sm leading-relaxed">
                {step === 1
                  ? "Start your journey as a verified Harappa bioscience seller"
                  : "Almost there! Confirm your email to activate your account"}
              </p>
            </div>

            {step === 1 && (
              <RegisterForm
                onSuccess={(data) => {
                  setRegData(data);
                  setStep(2);
                }}
              />
            )}
            {step === 2 && (
              <OTPVerify
                email={regData?.email}
                onSuccess={() =>
                  navigate("/login", { state: { verified: true } })
                }
              />
            )}

            <div className="relative my-8 flex items-center gap-4 text-slate-400 uppercase tracking-[0.2em] text-[10px] font-bold after:content-[''] after:h-px after:bg-slate-100 after:grow before:content-[''] before:h-px before:bg-slate-100 before:grow">
              or
            </div>

            <p className="text-center text-sm font-medium text-slate-500">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-primary font-bold hover:underline underline-offset-4"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
